// src/iap/capacitorIAP.ts
// Capacitor StoreKit2 IAP wrapper (plugin = InAppPurchase)

import { Capacitor } from "@capacitor/core";

type IAPNative = {
  getProducts: (opts: { productIds: string[] }) => Promise<{ products: any[] }>;
  purchaseProduct: (opts: { productId: string }) => Promise<any>;
  restorePurchases: () => Promise<{ purchases: any[] }>;
  addListener?: (event: string, cb: (e: any) => void) => { remove: () => void };
};

export type IAPProduct = {
  id: string;
  displayName?: string;
  description?: string;
  displayPrice?: string;
  // champs additionnels possibles selon l’impl
  title?: string;
  price?: string;
  currency?: string;
  priceValue?: number;
};

export type PurchaseResult = {
  status: "purchased" | "failed" | "pending";
  transactionId?: string;
  productId?: string;
  date?: string;
  // détails bruts renvoyés par le plugin Swift (utile debug / backend)
  transactionDetails?: Record<string, any>;
  errorCode?: string;
  errorMessage?: string;
};

function getPlugin(): IAPNative | null {
  const p = (window as any).Capacitor?.Plugins?.InAppPurchase;
  if (p && typeof p.getProducts === "function" && typeof p.purchaseProduct === "function") {
    return p as IAPNative;
  }
  return null;
}

export function ready(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios" && !!getPlugin();
}

export async function getProducts(productIds: string[]): Promise<IAPProduct[]> {
  const IAP = getPlugin();
  if (!IAP) throw new Error("InAppPurchase not available");
  (window as any).__debugLog?.(`[IAP] getProducts(${JSON.stringify(productIds)})`);
  const { products } = await IAP.getProducts({ productIds });
  (window as any).__debugLog?.(`[IAP] getProducts result count=${products?.length ?? 0}`);
  // On renvoie tel quel, en normalisant quelques alias courants
  return (products ?? []).map((p: any) => ({
    id: p.id ?? p.productId,
    displayName: p.displayName ?? p.title,
    description: p.description ?? p.localizedDescription,
    displayPrice: p.displayPrice ?? p.price ?? p.localizedPrice,
    // champs optionnels si présents
    price: p.price,
    currency: p.currency,
    priceValue: p.priceValue,
  }));
}

export async function purchase(productId: string): Promise<PurchaseResult> {
  const IAP = getPlugin();
  if (!IAP) throw new Error("InAppPurchase not available");
  (window as any).__debugLog?.(`[IAP] purchase start → ${productId}`);
  const res = await IAP.purchaseProduct({ productId });
  (window as any).__debugLog?.(`[IAP] purchase raw → ${JSON.stringify(res)}`);

  // Ton plugin Swift renvoie { status: "purchased" | "failed" | "pending", ... }
  if (res?.status === "purchased") {
    return {
      status: "purchased",
      transactionId: res.transactionId,
      productId: res.productId ?? productId,
      date: res.date,
      transactionDetails: res.transactionDetails,
    };
  }

  // Annulé / pending / erreur mappée par le plugin
  return {
    status: (res?.status as any) ?? "failed",
    errorCode: res?.errorCode ?? "UNKNOWN",
    errorMessage: res?.errorMessage ?? "Purchase not completed",
  };
}

export async function restore(): Promise<PurchaseResult[]> {
  const IAP = getPlugin();
  if (!IAP) throw new Error("InAppPurchase not available");
  (window as any).__debugLog?.(`[IAP] restorePurchases`);
  const r = await IAP.restorePurchases();
  const list = r?.purchases ?? [];
  (window as any).__debugLog?.(`[IAP] restore count=${list.length}`);
  return list.map((p: any) => ({
    status: "purchased",
    transactionId: p.transactionId,
    productId: p.productId,
    date: p.date,
    transactionDetails: p,
  }));
}

export function onIapLog(cb: (e: any) => void) {
  const IAP = getPlugin();
  return IAP?.addListener?.("iap_log", cb);
}
